import React, { useCallback, FocusEvent } from 'react';
import styled from 'styled-components';
import TextInput, { TextInputProps } from 'pureUi/TextInput';
import { Frame } from 'pureUi/Frame';
import { List } from 'pureUi/List';
import { pureUiTheme } from 'pureUi/pureUiTheme';

export interface PredictiveTextInputProps extends TextInputProps {
  options: string[][];
  onOptionSelect: (value: string) => void;
  showDropDown: boolean;
}

const PredictiveTextInput = (props: PredictiveTextInputProps) => {
  const { options, onOptionSelect, showDropDown, onFocus, ...textInputProps } = props;
  const handleOptionSelect = useCallback((value: string) => () => props.onOptionSelect(value), [props.onOptionSelect]);

  // pureUi/TextInput auto-selects all text on focus (e.target.select()).
  // For a predictive input this is unwanted — it causes context menus on
  // macOS/iOS (click-on-selected-text triggers Look Up) and replacing
  // the entire value on first keystroke. Undo the select by moving the
  // cursor to the end instead.
  const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const len = e.target.value.length;
    e.target.setSelectionRange(len, len);
    if (onFocus) {
      (onFocus as React.FocusEventHandler<HTMLInputElement>)(e);
    }
  }, [onFocus]);

  return (
    // A bug I raised in styled-compoenents years ago still appears to be an
    // issue today with no apparent fix.
    // https://github.com/styled-components/styled-components/issues/824
    // @ts-ignore
    <TextInput className={props.className} {...textInputProps} onFocus={handleFocus}>
      {!props.showDropDown || !props.options.length ? null : (
        <Frame className="dropDown">
          {props.options.map((group, index) => {
            return (
              <React.Fragment key={`${props.placeholder}-group-${index}`}>
                <ul className="listGroup">
                  {group.map(item => {
                    return (
                      <li key={item} tabIndex={0} onMouseDown={handleOptionSelect(item)} className="listGroupItem">
                        {item}
                      </li>
                    );
                  })}
                </ul>
              </React.Fragment>
            );
          })}
        </Frame>
      )}
    </TextInput>
  );
};

export default styled(PredictiveTextInput)`
  .dropDown {
    max-height: 50vh;
    overflow: auto;
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 100%;
    z-index: 1;
  }

  .listGroup {
    padding: 0;
    list-style: none;
    margin: 0;
    border-bottom: ${pureUiTheme.colorRoles.lightGreyBorder} 1px solid;

    &:last-of-type {
      border: none;
    }
  }

  .listGroupItem {
    padding: 20px 10px;
    text-transform: uppercase;
    color: ${pureUiTheme.colors.black};
    font-size: 14px;

    &:hover {
      cursor: pointer;
      background-color: ${pureUiTheme.colors.lightBlue};
    }
  }
`;
